import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, Trash2, RefreshCw, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 1500;

export function XeroInvoiceLinkBackfillPanel() {
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);
  const [blockerDiag, setBlockerDiag] = useState(null);
  const [blockerLoading, setBlockerLoading] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  // Cleanup on unmount.
  useEffect(() => stopPolling, [stopPolling]);

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setProgress(null);
    setResetResult(null);
    try {
      const { data } = await diagnosticsAPI.xeroLinkBackfillPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runReset = async () => {
    if (!window.confirm(
      'This will clear xero_invoice_id and xero_tenant_id on ALL source:bros ' +
      'invoices that were linked by the number matcher. Continue?'
    )) return;

    setResetting(true);
    setResetResult(null);
    try {
      const { data } = await diagnosticsAPI.xeroLinkBackfillReset();
      setResetResult(data);
      toast.success(`Reset complete: ${data.invoices_cleared} links cleared`);
      // Refresh preview to reflect cleared state.
      setPreview(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResetting(false);
    }
  };

  const runBlockerDiagnostic = async () => {
    setBlockerLoading(true);
    setBlockerDiag(null);
    try {
      const { data } = await diagnosticsAPI.xeroLinkBlockerDiagnostic();
      setBlockerDiag(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBlockerLoading(false);
    }
  };

  const busy = loading || resetting || polling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Xero Invoice Link Backfill</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
          <p className="text-xs text-amber-800 dark:text-amber-200">
            Number-based matching is retired. BR OS and Xero number invoices from
            independent series, so the invoice number is not a valid join key. The
            links produced by the number matcher were wrong. Use Reset to clear them.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            Preview
          </Button>
          <Button onClick={runReset} disabled={busy} size="sm" variant="destructive">
            {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Reset Number-Matched Links
          </Button>
        </div>

        {/* ── Reset result ── */}
        {resetResult && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              RESET COMPLETE
            </Badge>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Found</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(resetResult.total_found)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Invoices cleared</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(resetResult.invoices_cleared)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Bros docs cleared</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(resetResult.bros_invoices_cleared)}</p>
              </div>
            </div>

            {resetResult.cleared_details && resetResult.cleared_details.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Cleared invoices ({resetResult.cleared_details.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-left px-2 py-1.5 font-medium">bros_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">was_xero_invoice_id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resetResult.cleared_details.map((d, i) => (
                        <tr key={d.bros_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{d.invoice_number}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{d.bros_id || '-'}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{d.was_xero_invoice_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {resetResult.generated_at}
            </p>
          </div>
        )}

        {/* ── Preview result ── */}
        {preview && (
          <div className="space-y-4">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (DB-only, no Xero calls)
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Unlinked</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.total_unlinked)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Already linked</p>
                <p className="text-lg font-semibold tabular-nums text-blue-600">{fmtInt(preview.already_linked)}</p>
              </div>
            </div>

            {/* Already-linked invoices (the ones to reset) */}
            {preview.already_linked_details && preview.already_linked_details.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                  Already linked ({preview.already_linked_details.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">invoice_number</th>
                        <th className="text-left px-2 py-1.5 font-medium">xero_invoice_id</th>
                        <th className="text-left px-2 py-1.5 font-medium">xero_tenant_id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.already_linked_details.map((r, i) => (
                        <tr key={r.bros_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{r.invoice_number}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{r.xero_invoice_id || '-'}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{r.xero_tenant_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Generated: {preview.generated_at}
            </p>
          </div>
        )}

        {/* ── Xero Link Blocker Diagnostic ── */}
        <div className="pt-2 border-t space-y-3">
          <div>
            <h4 className="text-sm font-semibold">Xero Link Blocker Diagnostic</h4>
            <p className="text-xs text-muted-foreground">
              Read-only: last 3 BR OS invoice sync runs, how many bros_invoices have
              been self-healed with a Xero link, and a live 3-invoice sample straight
              from BR OS in this environment.
            </p>
          </div>
          <Button onClick={runBlockerDiagnostic} disabled={busy || blockerLoading} size="sm" variant="outline">
            {blockerLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Run Diagnostic
          </Button>

          {blockerDiag && (
            <div className="space-y-4">
              {blockerDiag.error ? (
                <p className="text-xs text-red-600">{blockerDiag.error}</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="border rounded-lg p-2 text-center">
                      <p className="text-xs text-muted-foreground">bros_invoices stamped</p>
                      <p className="text-lg font-semibold tabular-nums text-blue-600">
                        {fmtInt(blockerDiag.self_heal_evidence?.stamped_count)}
                      </p>
                    </div>
                    <div className="border rounded-lg p-2 text-center">
                      <p className="text-xs text-muted-foreground">Build commit</p>
                      <p className="text-sm font-mono">{blockerDiag.runtime_build?.commit || '-'}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Last 3 sync runs</p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">Timestamp</th>
                            <th className="text-left px-2 py-1.5 font-medium">Action</th>
                            <th className="text-left px-2 py-1.5 font-medium">Docs written</th>
                            <th className="text-left px-2 py-1.5 font-medium">Errors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(blockerDiag.last_sync_runs || []).slice(0, 3).map((run, i) => (
                            <tr key={run.timestamp || i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono text-[10px]">{run.timestamp}</td>
                              <td className="px-2 py-1">{run.action}</td>
                              <td className="px-2 py-1 tabular-nums">
                                {run.action === 'scheduled_invoice_import'
                                  ? `created ${fmtInt(run.created)}, updated ${fmtInt(run.updated)}`
                                  : `synced ${fmtInt(run.invoices_synced)}`}
                              </td>
                              <td className="px-2 py-1">
                                {run.failed
                                  ? <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">FAILED</Badge>
                                  : <span className="text-muted-foreground">none</span>}
                              </td>
                            </tr>
                          ))}
                          {(!blockerDiag.last_sync_runs || blockerDiag.last_sync_runs.length === 0) && (
                            <tr><td className="px-2 py-2 text-muted-foreground" colSpan={4}>No sync runs logged.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Live BR OS payload sample ({blockerDiag.live_bros_invoice_sample?.length || 0} invoice(s))
                    </p>
                    {blockerDiag.live_fetch_error && (
                      <p className="text-xs text-red-600 mb-1">{blockerDiag.live_fetch_error}</p>
                    )}
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">invoice_id</th>
                            <th className="text-left px-2 py-1.5 font-medium">xero_invoice_id present</th>
                            <th className="text-left px-2 py-1.5 font-medium">xero_invoice_id</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(blockerDiag.live_bros_invoice_sample || []).map((inv, i) => (
                            <tr key={inv.invoice_id || i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{inv.invoice_id}</td>
                              <td className="px-2 py-1">
                                {inv.has_xero_invoice_id_key
                                  ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">yes</Badge>
                                  : <Badge className="text-[10px] bg-muted text-muted-foreground">no</Badge>}
                              </td>
                              <td className="px-2 py-1 font-mono text-[10px]">{inv.xero_invoice_id || '-'}</td>
                            </tr>
                          ))}
                          {(!blockerDiag.live_bros_invoice_sample || blockerDiag.live_bros_invoice_sample.length === 0) && !blockerDiag.live_fetch_error && (
                            <tr><td className="px-2 py-2 text-muted-foreground" colSpan={3}>No sample returned.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <p className="text-[10px] text-muted-foreground">
                    Generated: {blockerDiag.generated_at}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
